import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Switch, TableRow, ClickAwayListener } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import { Skeleton } from '@material-ui/lab';
import CustomMenu from 'components/CustomMenu';
import { StyledTableCell } from 'screens/Team/TeamTasksListView/styles';
import styled, { css } from 'styled-components';
import { NewStyledIconButton, PaddingCSS, StyledTooltip, VerticallyAlignedFlexContainer } from 'styles/common';

import { getColorFromKey, haveSamePropValues } from 'constants/helper';
import { DownArrowIcon2, StyledIcon, UpArrowIcon2 } from 'constants/icons';
import theme from 'constants/theme';

import { Typography2 } from 'uiToolkit/Typography';

import { findGradesAndRolesOfFunction } from '../../FunctionAndGrade/Helpers';
import ExpandedTableWithinCard from '../../MeshTags/ExpandedTableInCard';
import { StyledTextField } from '../../MeshTags/styles';
import ClickToSetVerticalNavBar from '../CompetenciesByFunction/FunctionAndRollSelector/ClickToSetVerticalNavbar';

// helpers for this component only.
// keeping them here makes more sense than moving them up in helpers.
const MAX_EXPANSION_LEVEL = 4;

const DISPLAY_NAME_ERROR = 'DISPLAY_NAME_ERROR';
const NAME_ERROR = 'NAME_ERROR';

const getLeftPadding = (nestingLevel) => {
  const paddingFactor = 30;
  return `${nestingLevel * paddingFactor}px`;
};

const convertStateToTempState = (currentState) => {
  let tempState = {};
  currentState.forEach((v) => {
    tempState = {
      ...tempState,
      [v.function.uuid]: {
        grades: Array.isArray(v.grades) ? v.grades.reduce((a, b) => ({ ...a, [b.grade.uuid]: b.grade.uuid }), {}) : {},
      },
    };
  });
  return tempState;
};

const convertStateToTempStateNew = (currentState) => {
  let tempState = {};

  currentState.forEach((v) => {
    tempState = {
      ...tempState,
      [v.uuid]: {
        jobRoleList: Array.isArray(v.jobRoleList)
          ? v.jobRoleList.reduce((a, b) => ({ ...a, [b.uuid]: b.uuid }), {})
          : {},
      },
    };
  });
  return tempState;
};

const convertStateBackToArrays = (tempState) => {
  const convertedState = Object.keys(tempState)
    .map((k) => {
      const jobRoleList = Object.values(tempState[k].jobRoleList).map((v) => ({ uuid: v }));
      if (jobRoleList.length) {
        // if grades isn't an empty array
        return {
          uuid: k,
          jobRoleList,
        };
      }
      return null;
    })
    .filter((s) => s);
  return convertedState.length ? convertedState : null;
};

export const StyledVerticalLine = styled.span`
  position: absolute;
  top: -15px;
  bottom: ${(props) => (props.bottom ? props.bottom : '-1px')};
  left: ${(props) => (props.left ? props.left : 0)};
  display: inline-block;
  width: 1px;
  background-color: ${getColorFromKey('primary')};
`;

export const StyledHorizontalLine = styled.span`
  position: absolute;
  left: 0;
  top: 50%;
  display: inline-block;
  height: 1px;
  width: ${(props) => props.width};
  background-color: ${getColorFromKey('primary')};
`;

const getVerticalLines = (nestingLevel, isCreateNewRow = false) => {
  if (nestingLevel === 0) return null;

  return (
    <>
      {Array.from(Array(nestingLevel - 1).keys()).map((num) => (
        <StyledVerticalLine left={`${(num + 1) * -30}px`} />
      ))}
      <StyledHorizontalLine width="20px" />
      <StyledVerticalLine left="0" bottom={isCreateNewRow ? '50%' : '-1px'} />
    </>
  );
};

const showExpansionIcon = (nestingLevel) => nestingLevel < MAX_EXPANSION_LEVEL;

const ExpansionControl = ({
  nestingLevel,
  handleExpansionClick, // pass functions using callback hook to save renders.
  hasChildTags,
  isCreateNewRow,
  isExpanded,
}) => (
  <>
    {getVerticalLines(nestingLevel, isCreateNewRow)}
    <StyledExpansionContainer>
      {showExpansionIcon(nestingLevel) && (
        <>
          {hasChildTags && !isExpanded && (
            <NewStyledIconButton padding="none" height="max-content" onClick={handleExpansionClick} color="primary">
              <StyledExpansionIcon size="header">
                <ExpandMoreRoundedIcon />
              </StyledExpansionIcon>
            </NewStyledIconButton>
          )}
          {hasChildTags && isExpanded && (
            <NewStyledIconButton padding="none" height="max-content" onClick={handleExpansionClick} color="primary">
              <StyledIcon size="header">
                <ExpandMoreRoundedIcon />
              </StyledIcon>
            </NewStyledIconButton>
          )}
        </>
      )}
    </StyledExpansionContainer>
  </>
);

const TagSwitch = ({ active, handleToggleActive, isEditing }) => (
  <StyledTableCell paddingTop={!isEditing && '0'} paddingBottom={!isEditing && '0'} border>
    <Switch checked={active} onChange={handleToggleActive} color="primary" />
  </StyledTableCell>
);

const HashTagComp = ({
  name,
  handleKeyDown,
  handleTagChange,
  validationError,
  isCreateNewRow,
  children,
  nestingLevel,
  isEditing,
  isExpandable,
  errorMessage,
  hasError,
  errorType,
}) => (
  <StyledTableCell paddingtop="0" paddingBottom="0" paddingLeft={getLeftPadding(nestingLevel)} display="flex" border>
    <FlexContainer
      style={{ flexDirection: 'column', alignItems: 'start' }}
      // paddingLeft={isCreateNewRow && isExpandable ? '40px' : '20px'}
    >
      {children}
      <TextFieldContainer isEditing={isEditing}>
        <HashTagText>#</HashTagText>
        <StyledTextField
          name="name"
          placeholder="tag"
          fullWidth
          variant="outlined"
          value={name}
          onKeyDown={(event) => handleKeyDown({ event })}
          onChange={handleTagChange}
        />
      </TextFieldContainer>
      {validationError && <ErrorSubText center>Space and special characters are not allowed</ErrorSubText>}
      {hasError && errorType === NAME_ERROR && <ErrorSubText center>{errorMessage}</ErrorSubText>}
    </FlexContainer>
  </StyledTableCell>
);

const CompetencyNameComp = ({
  displayName,
  handleKeyDown,
  handleDisplayNameChange,
  validationError,
  isCreateNewRow,
  children,
  nestingLevel,
  isEditing,
  isExpandable,
  hasError,
  errorMessage,
  errorType,
}) => (
  <StyledTableCell paddingtop="0" paddingBottom="0" paddingLeft={getLeftPadding(nestingLevel)} display="flex" border>
    <FlexContainer
      style={{ flexDirection: 'column', alignItems: 'start' }}
      paddingLeft={isCreateNewRow && isExpandable ? '40px' : '20px'}
    >
      {children}
      <TextFieldContainer isEditing={isEditing}>
        <StyledTextField
          name="displayName"
          placeholder="Competency"
          fullWidth
          variant="outlined"
          value={displayName}
          onKeyDown={(event) => handleKeyDown({ event })}
          onChange={handleDisplayNameChange}
          autoFocus
        />
      </TextFieldContainer>
      {hasError && errorType === DISPLAY_NAME_ERROR && <ErrorSubText center>{errorMessage}</ErrorSubText>}
    </FlexContainer>
  </StyledTableCell>
);

const TagDescription = ({ description, handleKeyDown, handleOnChange, isEditing, allowDescriptionNewline = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setShowTooltip(false);
    }
  }, [isEditing]);
  return (
    <StyledTableCell style={{ padding: '18.5px 2px' }} paddingtop="0" paddingBottom="0" border>
      <StyledTooltip
        title={description}
        disableHoverListener
        open={showTooltip}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disableFocusListener={isEditing || !description}
      >
        <TextFieldContainer isEditing={isEditing}>
          <TextAreaInput
            placeholder="Description"
            name="description"
            fullWidth
            margin="none"
            variant="outlined"
            value={description || ''}
            onKeyDown={(event) => handleKeyDown({ event, isMultiline: true })}
            onChange={handleOnChange}
            multiline={allowDescriptionNewline}
            paddingLeft={0}
            paddingTop={0}
          />
        </TextFieldContainer>
      </StyledTooltip>
    </StyledTableCell>
  );
};

const Roles = ({
  handleOnChange,
  currentTag,
  functions,
  loading,
  selectAllLevels,
  functionsObject,
  removeAllLevels,
  isEditing,
}) => {
  const getSelectedRoleName = (jobFunctionRole) => {
    if (!jobFunctionRole) {
      return 'Everyone';
    }
    if (jobFunctionRole && Array.isArray(jobFunctionRole) && Array.isArray(functions)) {
      return jobFunctionRole
        .map((j) => {
          const { uuid, jobRoleList, roles } = j;
          const rolesMap = jobRoleList ?? roles;
          const selectedJobFunction = findGradesAndRolesOfFunction(functions, uuid);
          const jobLevelsUuids = rolesMap?.map((role) => role.uuid);
          const selectedJobLevels = selectedJobFunction?.roles
            .map((role) => {
              if (jobLevelsUuids?.includes(role.uuid)) {
                return role;
              }
            })
            .filter((role) => role);

          return `${selectedJobFunction.function.name}:${selectedJobLevels.map((l) => l.name).join(', ')}`;
        })
        .join(', ');
    }
  };

  const [showTooltip, setShowTooltip] = useState(false);

  const [menuOpened, setMenuOpened] = useState(false);

  const selectedRoles = getSelectedRoleName(currentTag.jobFunctionRole);

  if (loading) {
    return (
      <StyledTableCell paddingTop="0" paddingBottom="0" border>
        <Skeleton />
      </StyledTableCell>
    );
  }

  return (
    <StyledTableCell style={{ padding: '0.3125rem 1.5rem 0.3125rem 0.5rem' }} paddingTop="0" paddingBottom="0" border>
      <CustomMenu
        icon={
          <StyledTooltip
            title={selectedRoles}
            open={showTooltip}
            disableHoverListener
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <Icon isEditing={isEditing} menuOpened={menuOpened}>
              <Typography2
                truncate
                style={{
                  maxWidth: '85%',
                }}
              >
                {selectedRoles}
              </Typography2>
              {menuOpened ? (
                <UpArrowIcon2 color={theme.palette.icons.blue} size="tinier" />
              ) : (
                <DownArrowIcon2 color={theme.palette.icons.grey2} size="tinier" />
              )}
            </Icon>
          </StyledTooltip>
        }
        paperStyles={{
          boxShadow: '4px 2px 5px rgba(0, 0, 0, 0.11)',
          border: `1px solid ${theme.palette.borders.lighterGrey}`,
          borderRadius: '4px',
          overflow: 'auto',
        }}
        onOpen={() => {
          setMenuOpened(true);
          setShowTooltip(false);
        }}
        onClickAway={() => setMenuOpened(false)}
      >
        <ClickToSetVerticalNavBar
          functions={functions}
          loading={loading}
          handleOnChange={handleOnChange}
          currentTag={currentTag}
          selectAllLevels={selectAllLevels}
          removeAllLevels={removeAllLevels}
          functionsObject={functionsObject}
        />
      </CustomMenu>
    </StyledTableCell>
  );
};

const NewEditableCompetencyRow = ({
  tag,
  onUpdate: updateTag,
  nestingLevel = 0,
  isExpandable,
  isEditing,
  toggleEditing,
  // being not active is considered as deleted.
  onActiveToggle,
  hasChildTags = true,
  canHaveChildTags = true,
  enableExpansion: enableParentExpansion = () => {},
  disableExpansion: disableParentExpansion = () => {},
  allowDescriptionNewline = false,
  functions,
  loadingFunctions,
  isRolesEnabled = false,
}) => {
  const isCreateNewRow = !canHaveChildTags;
  const [currentTag, setCurrentTag] = useState(tag);
  const [validationError, setValidationError] = useState(false);

  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState(null);

  const [showExpandedCard, setShowExpandedCard] = useState(false);
  const [showChildTagCreationRow, setShowChildTagCreationRow] = useState(false);

  const functionsObject = useMemo(() => {
    if (Array.isArray(functions)) {
      return convertStateToTempState(functions);
    }
    return {};
  }, [functions]);

  const handleExpansionClick = (event) => {
    event.stopPropagation();
    setShowExpandedCard((prev) => !prev);
  };

  const handleAddClick = (event) => {
    event.stopPropagation();
    setShowChildTagCreationRow(true);
  };

  const handleFunctionLevel = (functionUuid, jobLevelUuid) => {
    if (!functionUuid) {
      setCurrentTag((curr) => ({ ...curr, jobFunctionRole: null }));
      return;
    }

    if (currentTag?.jobFunctionRole && Array.isArray(currentTag.jobFunctionRole)) {
      let tempState = convertStateToTempStateNew(currentTag.jobFunctionRole);
      if (tempState[functionUuid]) {
        // if function  already exists
        if (tempState[functionUuid].jobRoleList[jobLevelUuid]) {
          // if function and level already exists
          delete tempState[functionUuid].jobRoleList[jobLevelUuid];
        } else {
          // if only function exists
          tempState[functionUuid].jobRoleList = {
            ...tempState[functionUuid].jobRoleList,
            [jobLevelUuid]: jobLevelUuid,
          };
        }
      } else {
        tempState = {
          ...tempState,
          [functionUuid]: { jobRoleList: { [jobLevelUuid]: jobLevelUuid } },
        };
      }

      const convertedState = convertStateBackToArrays(tempState);

      setCurrentTag((curr) => ({ ...curr, jobFunctionRole: convertedState }));
    } else {
      const jobFunctionRole = [{ uuid: functionUuid, jobRoleList: [{ uuid: jobLevelUuid }] }];
      setCurrentTag((curr) => ({ ...curr, jobFunctionRole }));
    }
  };

  const handleSelectAllLevels = (functionUuid) => {
    const jobFunctionRole = {
      [functionUuid]: {
        jobRoleList: findGradesAndRolesOfFunction(functions, functionUuid)?.roles?.map(({ uuid }) => uuid),
      },
    };
    if (currentTag?.jobFunctionRole && Array.isArray(currentTag.jobFunctionRole)) {
      let tempState = convertStateToTempStateNew(currentTag.jobFunctionRole);

      tempState = { ...tempState, ...jobFunctionRole };

      const convertedState = convertStateBackToArrays(tempState);

      setCurrentTag((curr) => ({ ...curr, jobFunctionRole: convertedState }));
    } else {
      setCurrentTag((curr) => ({ ...curr, jobFunctionRole: convertStateBackToArrays(jobFunctionRole) }));
    }
  };

  const handleRemoveAllLevels = (functionUuid) => {
    if (currentTag?.jobFunctionRole && Array.isArray(currentTag.jobFunctionRole)) {
      const tempState = convertStateToTempStateNew(currentTag.jobFunctionRole);
      delete tempState[functionUuid];
      const convertedState = convertStateBackToArrays(tempState);

      setCurrentTag((curr) => ({ ...curr, jobFunctionRole: convertedState }));
    }
  };

  const { active, name, description, displayName } = currentTag;

  const handleOnChange = useCallback(({ target: { name, value } }) => {
    setCurrentTag((oldTag) => ({ ...oldTag, [name]: value }));
  }, []);

  const handleToggleActive = useCallback(
    (event) => {
      onActiveToggle(event, tag);
    },
    [tag]
  );

  const handleDisplayNameChange = useCallback(
    ({ target }) => {
      setValidationError(false);

      handleOnChange({ target });
    },
    [handleOnChange]
  );

  const handleTagChange = useCallback(
    ({ target }) => {
      const regex = /^[a-zA-Z0-9_\b\*]+$/;
      setValidationError(false);

      if (target.value === '' || regex.test(target.value)) {
        handleOnChange({ target });
      } else {
        setValidationError(true);
        setTimeout(() => setValidationError(false), 2500);
      }
    },
    [handleOnChange]
  );

  const handleKeyDown = ({ event: { keyCode, key, target } }) => {
    if (keyCode === 27) {
      // this will work since everytime update is passed up to parent.
      target.blur();
      cancelEditing();
    }
    if (key === 'Enter' && !allowDescriptionNewline) {
      updateTag(currentTag);
    }
  };

  const cancelEditing = () => {
    setCurrentTag(tag);
    if (isEditing) toggleEditing(tag.id);
  };

  // listen to changes in parent tags array.
  useEffect(() => {
    setCurrentTag(tag);
  }, [tag.active, tag.name, tag.description, tag.id, isEditing]);

  const enableEditing = useCallback(() => {
    if (!isEditing) toggleEditing(tag.id);
  }, [tag.id, isEditing]);

  const handleClickAway = useCallback(() => {
    const propertyList = ['name', 'description', 'jobFunctionRole', 'displayName'];
    if (haveSamePropValues(tag, currentTag, propertyList)) cancelEditing();
    else {
      setHasError(true);
      if (!currentTag.displayName) {
        setErrorMessage('This is a required field');
        setErrorType(DISPLAY_NAME_ERROR);
      } else if (!currentTag.name) {
        setErrorMessage('This is a required field');
        setErrorType(NAME_ERROR);
      } else {
        updateTag(currentTag);
      }
    }
  }, [currentTag, updateTag]);

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <StyledTableRow isEditing={isEditing} onClick={enableEditing}>
          {/* {
        isExpandable && 
          <ExpansionControl 
            nestingLevel={nestingLevel}
            handleExpansionClick={handleExpansionClick}
            handleAddChild={handleAddClick}
            hasChildTags={hasChildTags}
            canHaveChildTags={canHaveChildTags}
            isExpanded={showExpandedCard}
          />
      } */}
          {/* <TagSwitch
        isEditing={isEditing}
        active={active} 
        handleToggleActive={handleToggleActive} 
      /> */}
          <CompetencyNameComp
            isEditing={isEditing}
            displayName={displayName}
            handleDisplayNameChange={handleDisplayNameChange}
            handleKeyDown={handleKeyDown}
            nestingLevel={nestingLevel}
            isCreateNewRow={isCreateNewRow}
            isExpandable={isExpandable}
            errorMessage={errorMessage}
            hasError={hasError}
            errorType={errorType}
          >
            {isExpandable && (
              <ExpansionControl
                nestingLevel={nestingLevel}
                handleExpansionClick={handleExpansionClick}
                handleAddChild={handleAddClick}
                hasChildTags={hasChildTags}
                isCreateNewRow={isCreateNewRow}
                canHaveChildTags={false}
                isExpanded={showExpandedCard}
              />
            )}
          </CompetencyNameComp>

          <HashTagComp
            isEditing={isEditing}
            name={name}
            handleTagChange={handleTagChange}
            handleKeyDown={handleKeyDown}
            validationError={validationError}
            nestingLevel={nestingLevel}
            isCreateNewRow={isCreateNewRow}
            isExpandable={isExpandable}
            errorMessage={errorMessage}
            hasError={hasError}
            errorType={errorType}
          />
          {isRolesEnabled && (
            <Roles
              isEditing={isEditing}
              handleKeyDown={handleFunctionLevel}
              handleOnChange={handleFunctionLevel}
              currentTag={currentTag}
              functions={functions}
              loading={loadingFunctions}
              selectAllLevels={handleSelectAllLevels}
              removeAllLevels={handleRemoveAllLevels}
              functionsObject={functionsObject}
            />
          )}
          <TagDescription
            isEditing={isEditing}
            description={description}
            handleKeyDown={handleKeyDown}
            handleOnChange={handleOnChange}
            allowDescriptionNewline={allowDescriptionNewline}
          />

          <StyledTableCell textAlign="right" paddingTop={!isEditing && '0'} paddingBottom={!isEditing && '0'} border>
            {!isEditing ? null : (
              <NewStyledIconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleActive(event);
                }}
              >
                <StyledIcon size="header" color="primary">
                  <DeleteIcon />
                </StyledIcon>
              </NewStyledIconButton>
            )}
          </StyledTableCell>
        </StyledTableRow>
      </ClickAwayListener>

      {(showExpandedCard || showChildTagCreationRow) && (
        <ExpandedTableWithinCard
          parentTag={tag}
          nestingLevel={nestingLevel + 1}
          showExpandedCard={showExpandedCard}
          enableParentExpansion={enableParentExpansion}
          disableParentExpansion={disableParentExpansion}
        />
      )}
    </>
  );
};

const HashTagText = styled.span`
  // padding-top: 6px;
  display: inline-block;
  padding-right: 2px;
`;

const ErrorSubText = styled.span`
  font-size: 12px;
  margin: -5px 0 0 0;
  display: inline-block;
  color: red;
  opacity: 0.75;
  line-height: 1rem;
`;

const StyledExpansionIcon = styled(StyledIcon)`
  transform: rotate(-90deg);
`;

const StyledExpansionContainer = styled.div`
  display: flex;
  align-items: center;
  ${PaddingCSS}
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  ${PaddingCSS};
`;

const StyledTableRow = styled(TableRow)`
  ${(props) =>
    props.isEditing &&
    css`
      background-color: #e9f6ff;
    `}
`;

const TextFieldContainer = styled.div`
  display: flex;
  align-items: center;

  flex: 1;

  margin-top: 0.5rem;
  margin-bottom: 0.5rem;

  padding-left: 0.25rem;
  min-height: 2.5rem;

  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${props.theme.palette.background.paper};
      border-radius: 4px;
      border: 1px solid ${theme.palette.borders.lighterGrey};
    `};
  width: 100%;
`;

const TextAreaInput = styled(StyledTextField)`
  & .MuiOutlinedInput-multiline {
    padding: 0 0.65rem;
    font-size: 0.875rem;
  }
  & .MuiOutlinedInput-inputMultiline {
    padding: 5px 0;
  }
`;

const Icon = styled(VerticallyAlignedFlexContainer)`
  justify-content: space-between;
  align-items: center;
  background-color: ${theme.palette.background.paper};
  padding: 0.35rem 0.5rem;
  border-radius: 4px;
  min-height: 2.5rem;
  cursor: pointer;
  max-width: 23rem;
  ${(props) =>
    props.isEditing &&
    css`
      border: 1px solid ${theme.palette.borders.lighterGrey};
    `};
  ${(props) =>
    props.menuOpened &&
    css`
      border: 1px solid ${theme.palette.borders.primary};
    `};
`;
export default NewEditableCompetencyRow;
